import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";
import { GROUPS, productsByGroup } from "@/lib/products";
import { ProductModal } from "@/components/site/ProductModal";
import { waLink, productWaMsg } from "@/lib/site";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export default function ProductGroupPage() {
  const { slug } = useParams();
  const group = GROUPS.find((g) => g.slug === slug);
  const [selected, setSelected] = useState(null);

  if (!group) return <Navigate to="/catalogue" replace />;
  const list = productsByGroup(group);
  const isRte = group.slug === "ready-to-eat";

  return (
    <div className="bg-cream">
      <section className="bg-navy pb-14 pt-32 text-white md:pt-40">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gold">
              <Link to="/catalogue" className="transition-colors duration-300 hover:text-white">Products</Link>
              <span className="mx-2 text-white/40">/</span>
              {group.name}
            </p>
            <div className="mt-6 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
              <div className="max-w-2xl">
                <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
                  {group.name}
                </h1>
                <p className="mt-5 text-base leading-relaxed text-white/70 md:text-lg">
                  {group.description}
                </p>
                {list.length > 0 && (
                  <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    {list.length} products — pricing & MOQs on request
                  </p>
                )}
              </div>
              <div className="hidden shrink-0 overflow-hidden rounded-2xl border border-white/15 md:block">
                <img src={group.image} alt={`${group.name} — RBL EXIM`} className="h-44 w-72 object-cover" />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 md:px-10 md:py-16">
        {list.length > 0 ? (
          <div data-testid="group-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((p, i) => (
              <FadeUp key={p.slug} delay={Math.min(i, 8) * 0.04}>
                <article
                  data-testid={`product-card-${p.slug}`}
                  onClick={() => setSelected(p)}
                  className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10"
                >
                  <div className="relative overflow-hidden bg-white">
                    <img
                      src={p.image}
                      alt={`${p.name} — RBL EXIM ${group.name}`}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    {isRte && (
                      <span className="absolute left-3 top-3 rounded-full bg-navy/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-gold">
                        Private Label Available
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-lg font-semibold tracking-tight text-navy">
                        {p.name}
                      </h3>
                      {p.pack && (
                        <span className="shrink-0 rounded-full bg-gold-soft px-2.5 py-1 text-[11px] font-semibold text-navy">
                          {p.pack}
                        </span>
                      )}
                    </div>
                    {p.brand && (
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-dark">
                        by {p.brand}
                      </p>
                    )}
                    <p className="mt-2 flex-1 text-xs leading-relaxed text-navy/60">
                      {group.productLine}
                    </p>
                    <div className="mt-4 flex flex-col gap-2 pt-1">
                      <Link
                        to={`/?product=${encodeURIComponent(p.name + (p.pack ? ` (${p.pack})` : ""))}&category=${encodeURIComponent(group.name)}#enquire`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`product-enquire-${p.slug}`}
                        className="flex items-center justify-center gap-1.5 rounded-full bg-navy py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
                      >
                        Enquire Now <ArrowUpRight size={13} />
                      </Link>
                      <a
                        href={waLink(productWaMsg(p.name + (p.pack ? ` (${p.pack})` : ""), group.name))}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`product-whatsapp-${p.slug}`}
                        className="flex items-center justify-center gap-1.5 rounded-full border border-[#25D366]/50 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#128C4A] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
                      >
                        <WhatsAppIcon /> WhatsApp Enquiry
                      </a>
                    </div>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        ) : (
          <FadeUp>
            <div className="mx-auto max-w-2xl rounded-2xl border border-navy/10 bg-white p-10 text-center">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-navy">
                Sourced to your specification
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-navy/65">
                Products in this group are sourced and packed according to
                buyer requirements. Share your product list and specifications,
                and our team will respond with availability, packaging options
                and pricing.
              </p>
              <Link
                to={`/?category=${encodeURIComponent(group.name)}#enquire`}
                data-testid="group-sourcing-cta"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-gold hover:text-navy"
              >
                Request a Quote <ArrowUpRight size={15} />
              </Link>
            </div>
          </FadeUp>
        )}

        <FadeUp>
          <div className="mt-16 flex flex-col items-center justify-between gap-6 rounded-2xl bg-navy p-8 text-white md:flex-row md:p-12">
            <div>
              <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">
                Need specifications, MOQs or pricing for {group.shortName}?
              </h2>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/70">
                Tell us what you need and our team will respond with product
                details matched to your market.
              </p>
            </div>
            <Link
              to={`/?category=${encodeURIComponent(group.name)}#enquire`}
              data-testid="group-quote-cta"
              className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-white"
            >
              Request Product Details
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
      </section>

      <AnimatePresence>
        {selected && <ProductModal product={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
